import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import LogoUploader from "@/components/LogoUploader";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, MapPin, Phone, BookOpen, Lock, AlertCircle, Pencil, RefreshCcw, Eye, QrCode } from "lucide-react";
import EntityDetailDialog from "@/components/EntityDetailDialog";
import { useAuth } from "@/contexts/AuthContext";

export default function BranchesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedSchool, allBranches, refresh, loading } = useBranch();
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", contact_phone: "", principal_name: "", payment_account_name: "", payment_details_text: "" });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [qrBranch, setQrBranch] = useState(null);

  const branchesOfSchool = selectedSchool
    ? allBranches.filter((b) => b.school_id === selectedSchool.id)
    : [];

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", address: "", contact_phone: "", principal_name: "", payment_account_name: "", payment_details_text: "" });
    setOpenForm(true);
  };

  const openEdit = (branch) => {
    setEditing(branch);
    setForm({
      name: branch.name || "",
      address: branch.address || "",
      contact_phone: branch.contact_phone || "",
      principal_name: branch.principal_name || "",
      payment_account_name: branch.payment_account_name || "",
      payment_details_text: branch.payment_details_text || "",
    });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/branches/${editing.id}`, form);
        toast.success(editing.status === "rejected" ? "Branch resubmitted for approval ✨" : "Branch updated");
      } else {
        await api.post("/branches", { school_id: selectedSchool.id, ...form });
        toast.success(`Branch ${form.name} submitted for approval!`);
      }
      setForm({ name: "", address: "", contact_phone: "", principal_name: "", payment_account_name: "", payment_details_text: "" });
      setOpenForm(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const canAddBranch = selectedSchool?.status === "approved";

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">List of Branches</h1>
          <p className="text-brand-inkMuted mt-1">
            {selectedSchool ? <>Branches of <span className="font-semibold text-foreground">{selectedSchool.name}</span></>
              : "Pick a school from the header to see and manage its branches."}
          </p>
        </div>
        {canAddBranch && (
          <Button
            onClick={openCreate}
            className="rounded-full bg-brand-orange hover:bg-brand-orangeHover text-foreground shadow-sm"
            data-testid="add-branch-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add branch
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-brand-inkMuted font-heading">Loading...</div>
      ) : !selectedSchool ? (
        <LockedCard
          title="No school selected"
          desc="Use the school dropdown in the header, or register your first school."
          icon={AlertCircle}
          cta={<Link to="/school-profile"><Button className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white mt-3">Manage schools</Button></Link>}
        />
      ) : selectedSchool.status !== "approved" ? (
        <LockedCard
          title="Branches unlock after approval"
          desc={`${selectedSchool.name} is ${selectedSchool.status}. Once approved by a Super Admin, you can add branches.`}
          icon={Lock}
        />
      ) : (
        branchesOfSchool.length === 0 ? (
          <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branches">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
              <Building2 className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No branches yet for {selectedSchool.name}</p>
            <p className="text-brand-inkMuted">Click &ldquo;Add branch&rdquo; to register your first one.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {branchesOfSchool.map((b) => (
              <div
                key={b.id}
                className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300"
                data-testid={`branch-card-${b.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  {b.status === "approved" ? (
                    <LogoUploader
                      kind="branches"
                      id={b.id}
                      logo={b.logo_base64}
                      size="md"
                      onUpdated={() => refresh()}
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-brand-purple" />
                    </div>
                  )}
                  <StatusBadge status={b.status} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground">{b.name}</h3>
                <p className="text-sm text-brand-inkMuted mt-1 flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />{b.address}
                </p>
                <p className="text-sm text-brand-inkMuted mt-1 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />{b.contact_phone}
                </p>
                {b.principal_name && <p className="text-sm text-brand-inkMuted mt-1">Principal: {b.principal_name}</p>}
                {b.status === "rejected" && b.rejection_reason && (
                  <div className="mt-3 rounded-xl bg-[#FFEBEE] text-[#C62828] px-3 py-2 text-xs">{b.rejection_reason}</div>
                )}
                {(b.status === "rejected" || b.status === "pending") && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(b)}
                    className={`mt-3 w-full rounded-full text-xs h-9 ${
                      b.status === "rejected"
                        ? "border-[#C62828]/40 text-[#C62828] hover:bg-[#FFEBEE]"
                        : "border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight"
                    }`}
                    data-testid={`branch-${b.status === "rejected" ? "resubmit" : "edit"}-btn-${b.id}`}
                  >
                    {b.status === "rejected" ? (
                      <><RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Edit & Resubmit</>
                    ) : (
                      <><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</>
                    )}
                  </Button>
                )}
                {b.status === "approved" && (
                  <>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => { setDetailId(b.id); setDetailOpen(true); }}
                      className="rounded-full text-xs h-9 border-[#BFDBFE] text-[#1D4ED8] hover:bg-[#EFF6FF]"
                      data-testid={`branch-view-${b.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <Button
                      onClick={() => navigate(`/branches/${b.id}`)}
                      className="rounded-full bg-brand-purpleLight text-brand-purpleHover hover:bg-brand-purple hover:text-white text-xs h-9"
                      data-testid={`branch-manage-${b.id}`}
                    >
                      <BookOpen className="w-3.5 h-3.5 mr-1" /> Manage
                    </Button>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setQrBranch(b)}
                    className="mt-2 w-full rounded-full text-xs h-9 border-brand-purple/25 text-brand-purple hover:bg-brand-purpleLight"
                    data-testid={`branch-qr-${b.id}`}
                  >
                    <QrCode className="w-3.5 h-3.5 mr-1.5" /> Enrollment QR
                  </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )
      )}

      <Dialog open={openForm} onOpenChange={(o) => { setOpenForm(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-lg" data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              {editing ? (
                editing.status === "rejected"
                  ? (<><RefreshCcw className="w-5 h-5 text-[#C62828]" /> Edit & Resubmit Branch</>)
                  : (<><Pencil className="w-5 h-5 text-brand-purple" /> Edit Branch</>)
              ) : "Register a branch"}
            </DialogTitle>
            <DialogDescription>
              {editing?.status === "rejected"
                ? "Update the details and the branch will go back to pending for super-admin re-review."
                : editing?.status === "approved"
                ? <>Update operational details for this branch under <strong>{selectedSchool?.name}</strong>. No re-approval needed.</>
                : <>New branch under <strong>{selectedSchool?.name}</strong>. Super Admins will review it.</>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="branch-form">
            <div>
              <Label className="font-heading">Branch name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                placeholder="Downtown Branch" data-testid="branch-name-input" />
            </div>
            <div>
              <Label className="font-heading">Principal (optional)</Label>
              <Input value={form.principal_name} onChange={(e) => setForm({ ...form, principal_name: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                placeholder="Ms. Joy" data-testid="branch-principal-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-heading">Address</Label>
              <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                placeholder="456 Cloud Ave" data-testid="branch-address-input" />
            </div>
            <div>
              <Label className="font-heading">Phone</Label>
              <Input required value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                placeholder="+1 555 0200" data-testid="branch-phone-input" />
            </div>
            <div className="sm:col-span-2 border-t border-brand-purple/10 pt-4 mt-1">
              <p className="font-heading text-sm font-semibold text-foreground">Statement of Account — Branch Payment Info</p>
              <p className="text-[11px] text-brand-inkMuted mt-0.5">Overrides the school-wide payment details on this branch&apos;s SOA printouts. Leave blank to fall back to the school defaults.</p>
            </div>
            <div>
              <Label className="font-heading">Account Name (branch)</Label>
              <Input value={form.payment_account_name || ""}
                onChange={(e) => setForm({ ...form, payment_account_name: e.target.value })}
                maxLength={120}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                placeholder="e.g. Juana Dela Cruz"
                data-testid="branch-payment-account-name-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-heading">Payment / Bank Details</Label>
              <Textarea value={form.payment_details_text || ""}
                onChange={(e) => setForm({ ...form, payment_details_text: e.target.value })}
                maxLength={2000}
                rows={4}
                className="rounded-2xl mt-1.5 border-brand-purple/20 font-mono text-sm"
                placeholder={"BDO - 011108001607 (Juana Dela Cruz)\nGCash - 09179832931\nPayMaya - 09179832931"}
                data-testid="branch-payment-details-input" />
              <p className="text-[10.5px] text-brand-inkMuted mt-1">One line per account. Rendered as-is on the SOA footer.</p>
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)}
                className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy}
                className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white"
                data-testid="branch-submit-btn">
                {busy
                  ? "Submitting..."
                  : editing
                    ? (editing.status === "rejected" ? "Resubmit for approval" : "Save changes")
                    : "Submit branch"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <EntityDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        kind="branch"
        id={detailId}
        role={user?.role}
        onEdit={(b) => { setDetailOpen(false); openEdit(b); }}
        onChanged={refresh}
      />

      {qrBranch && <EnrollmentQrDialog branch={qrBranch} onClose={() => setQrBranch(null)} />}
    </>
  );
}

function EnrollmentQrDialog({ branch, onClose }) {
  const [meta, setMeta] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/branches/${branch.id}/qr-url`);
        if (mounted) setMeta(data);
      } catch (e) {
        if (mounted) setLoadErr(formatApiError(e));
      }
    })();
    return () => { mounted = false; };
  }, [branch.id]);

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const pngUrl = `${apiBase}${meta?.qr_png_path || `/api/branches/${branch.id}/qr.png`}`;
  const posterUrl = `${apiBase}/api/branches/${branch.id}/enrollment-poster.pdf`;
  const enrollUrl = meta?.enroll_url || "";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(enrollUrl);
      toast.success("Enrollment link copied");
    } catch { toast.error("Could not copy"); }
  };

  const printNow = () => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("Pop-up blocked"); return; }
    // Build the print document via DOM APIs (textContent) to avoid HTML
    // injection from values that ultimately come from the DB (branch.name,
    // enrollUrl). Never interpolate untrusted strings into document.write.
    const doc = win.document;
    doc.open();
    doc.write("<!DOCTYPE html><html><head></head><body></body></html>");
    doc.close();

    const titleEl = doc.createElement("title");
    titleEl.textContent = `Enrollment QR — ${branch.name}`;
    doc.head.appendChild(titleEl);

    const style = doc.createElement("style");
    style.textContent = `
      body{font-family:Helvetica,Arial,sans-serif;text-align:center;padding:40px;color:#1F1235}
      h1{font-size:22px;margin:8px 0}
      p{color:#6B6680;margin:4px 0}
      img{max-width:380px;width:100%;margin:24px auto;display:block}
      .url{font-family:monospace;font-size:11px;color:#5D2E8E;word-break:break-all}
      @page { size: A4 portrait; margin: 20mm; }
    `;
    doc.head.appendChild(style);

    const eyebrow = doc.createElement("p");
    eyebrow.setAttribute("style", "font-size:11px;letter-spacing:.2em;text-transform:uppercase");
    eyebrow.textContent = "Student Enrollment";
    doc.body.appendChild(eyebrow);

    const h1 = doc.createElement("h1");
    h1.textContent = branch.name || "";
    doc.body.appendChild(h1);

    const sub = doc.createElement("p");
    sub.textContent = "Scan this QR with your phone to enroll";
    doc.body.appendChild(sub);

    const img = doc.createElement("img");
    img.src = pngUrl;
    img.alt = "QR";
    doc.body.appendChild(img);

    const urlP = doc.createElement("p");
    urlP.className = "url";
    urlP.textContent = enrollUrl;
    doc.body.appendChild(urlP);

    const foot = doc.createElement("p");
    foot.setAttribute("style", "margin-top:30px;font-size:11px");
    foot.textContent = "Powered by SchoolBoss";
    doc.body.appendChild(foot);

    // Defer print so images can lay out.
    setTimeout(() => { try { win.focus(); win.print(); } catch { /* ignore */ } }, 300);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="branch-qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><QrCode className="w-6 h-6 text-brand-purple" /> Enrollment QR</DialogTitle>
          <DialogDescription>Print or share this code so parents can self-enroll students.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-brand-inkMuted">Print or share this QR. Anyone who scans it will land on a mobile-friendly enrollment form for <strong className="text-foreground">{branch.name}</strong>.</p>
          {loadErr ? (
            <div className="rounded-2xl bg-[#FDECEA] border border-[#F8B4B0] p-3 text-sm text-[#C62828]" data-testid="branch-qr-err">{loadErr}</div>
          ) : (
            <div className="rounded-2xl border border-brand-purple/15 bg-white p-5 flex flex-col items-center">
              <img src={pngUrl} alt={`Enrollment QR for ${branch.name}`} className="w-56 h-56 object-contain" data-testid="branch-qr-img" />
              <p className="mt-3 text-xs text-center text-brand-inkMuted font-mono break-all" data-testid="branch-qr-url">{enrollUrl || "—"}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={copyUrl} className="rounded-2xl" data-testid="branch-qr-copy">Copy Link</Button>
            <a href={pngUrl} download={`enrollment-qr-${branch.id}.png`}>
              <Button variant="outline" className="rounded-2xl" data-testid="branch-qr-download">Download PNG</Button>
            </a>
            <a href={posterUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-2xl border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight" data-testid="branch-qr-poster">Print Poster (PDF)</Button>
            </a>
            <Button onClick={printNow} className="rounded-2xl bg-brand-purple text-white" data-testid="branch-qr-print">Quick Print</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LockedCard({ title, desc, cta, icon: Icon = Lock }) {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="locked-card">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">{title}</p>
      <p className="text-brand-inkMuted mt-1">{desc}</p>
      {cta}
    </div>
  );
}
